import React, { createContext, useContext, useState } from 'react';
import { sampleProducts } from '../data/sampleProducts';
import { extendedProducts } from '../data/extendedProducts';

const DatabaseContext = createContext();

export const useDatabase = () => {
  const context = useContext(DatabaseContext);
  if (!context) {
    throw new Error('useDatabase must be used within a DatabaseProvider');
  }
  return context;
};

export const DatabaseProvider = ({ children }) => {
  const [products] = useState([...sampleProducts, ...extendedProducts]);
  const [loading, setLoading] = useState(false);

  const searchProducts = (query, filters = {}) => {
    setLoading(true);
    
    return new Promise((resolve) => {
      setTimeout(() => {
        let results = [...products];

        if (query) {
          const searchTerm = query.toLowerCase();
          results = results.filter(product =>
            product.manufacturer_sku.toLowerCase().includes(searchTerm) ||
            product.normalized_sku.toLowerCase().includes(searchTerm) ||
            product.manufacturer.toLowerCase().includes(searchTerm) ||
            JSON.stringify(product.specifications).toLowerCase().includes(searchTerm)
          );
        }

        // Apply filters
        if (filters.category) {
          results = results.filter(p => p.etim_class_code === filters.category);
        }

        if (filters.manufacturer) {
          results = results.filter(p => p.manufacturer === filters.manufacturer);
        }

        if (filters.dimensions) {
          Object.entries(filters.dimensions).forEach(([key, range]) => {
            results = results.filter(p => {
              const value = p.specifications[key];
              return value && value >= range.min && value <= range.max;
            });
          });
        }

        setLoading(false);
        resolve(results);
      }, 300);
    });
  };

  const getProductById = (id) => {
    return products.find(product => product.id === id);
  };

  const getCategories = () => {
    const categories = [...new Set(products.map(p => p.etim_class_code))];
    return categories.map(code => ({
      code,
      name: getCategoryName(code),
      count: products.filter(p => p.etim_class_code === code).length
    }));
  };

  const getCategoryName = (code) => {
    const categoryNames = {
      'EC000030': 'Rillenkugellager',
      'EC000031': 'Schrägkugellager',
      'EC000032': 'Nadellager',
      'EC000033': 'Axiallager',
      'EC000034': 'Gehäuselager',
      'EC000035': 'Kegelrollenlager',
      'EC000924': 'Profilschienenführungen',
      'EC002306': 'Radialwellendichtringe',
      'EC002307': 'O-Ringe',
      'EC001234': 'Keilriemen',
      'EC001235': 'Zahnriemen'
    };
    return categoryNames[code] || 'Unbekannte Kategorie';
  };

  const getManufacturers = () => {
    return [...new Set(products.map(p => p.manufacturer))];
  };

  const value = {
    products,
    loading,
    searchProducts,
    getProductById,
    getCategories,
    getManufacturers,
    getCategoryName
  };

  return (
    <DatabaseContext.Provider value={value}>
      {children}
    </DatabaseContext.Provider>
  );
};